/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Modal } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { role } from '@/client';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useUpdateUserSubtenantsMutation } from '@/hooks/mutation/useUpdateUserSubtenantsMutation';
import { useUserByIdQuery } from '@/hooks/query/useUserByIdQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { UserSubtenantsHierarchy } from '@/pages/Users/UserSubtenantsHierarchy';
import { Avatar } from '@/primitives/Avatar';
import { Container } from '@/primitives/Container';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export function UpdateUserSubtenantsModal() {
  const { t } = useMimicTranslation('users');
  const navigate = useNavigate();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const { userID } = useParams<'userID'>();
  const [message] = useMessage();

  const [userSubtenantRoles, setUserSubtenantRoles] = useState<Array<{ subtenantId: string; role: role }>>([]);

  const [open, setOpen] = useState(true);

  const userQuery = useUserByIdQuery({ userId: userID! });
  const displayName = userQuery.data?.data.displayName;

  const { mutate: updateUserSubtenantsMutation } = useUpdateUserSubtenantsMutation({
    onError: () => {
      message.error(t('feedback.errorOnUpdate', { user: displayName }));
    },
    onSuccess: () => {
      message.success(t('feedback.userAdded', { user: displayName }));
    },
  });

  const updateUserSubtenants = (userId: string, subtenants: Array<{ subtenantId: string; role: role }>) => {
    updateUserSubtenantsMutation({ id: userId, tenantId: tenantID!, requestBody: { subtenants } });
  };

  const closeModal = () => {
    setOpen(false);
    afterClose();
  };

  const afterClose = () => {
    navigate(-1);
  };

  const handleUpdateUserSubtenantRole = ({
    subtenantId,
    userSubtenantRole,
  }: {
    subtenantId: string;
    userSubtenantRole: role;
  }) => {
    setUserSubtenantRoles((prev) => {
      const ids = prev.map((usr) => usr.subtenantId);

      if (prev.length === 0) {
        return [{ subtenantId, role: userSubtenantRole }];
      }

      if (!ids.includes(subtenantId)) {
        return [...prev, { subtenantId, role: userSubtenantRole }];
      }

      return prev.map((usr) => {
        if (usr.subtenantId === subtenantId) {
          return { ...usr, role: userSubtenantRole };
        }
        return usr;
      });
    });
  };

  return (
    <Modal
      title={t('updateSubtenants.assignSubtenantsAndRoles')}
      centered
      afterClose={afterClose}
      open={open}
      onOk={() => updateUserSubtenants(userID!, userSubtenantRoles)}
      okText={t('updateSubtenants.assign')}
      okButtonProps={{ disabled: userSubtenantRoles.length === 0 }}
      onCancel={closeModal}
    >
      <Container>
        <QueryWrapper queryResult={userQuery}>
          {(userData) => {
            return (
              <>
                <Text>{t('updateSubtenants.assignRolesDescription')}</Text>
                <Flex align="center" gap="xs">
                  <Avatar
                    color="rgba(25, 25, 25, 0.65)"
                    backgroundColor="rgba(25, 25, 25, 0.14)"
                    text={userData.data.displayName[0] ?? ''}
                  />
                  <Text>{userData.data.displayName}</Text>
                </Flex>
                <UserSubtenantsHierarchy
                  userId={userID!}
                  handleUpdateUserSubtenantRole={handleUpdateUserSubtenantRole}
                />
              </>
            );
          }}
        </QueryWrapper>
      </Container>
    </Modal>
  );
}
